<template>
  <div class="reset-password">
    <FormElement
      :title="isStepOne ? $t('reset_password.mail_code') : $t('reset_password.new_login')"
      :message="
        isStepOne
          ? `${$t('reset_password.mail_sent')} ${email}`
          : $t('reset_password.password_error')
      "
    >
      <div
        v-if="isStepOne"
        class="form-input"
        :class="[
          { 'form-input--full': form.password_reset_code },
          {
            'form-input--error':
              errors.password_reset_code || (authErrors && authErrors.password_reset_code),
          },
        ]"
      >
        <input v-model="form.password_reset_code" type="text" />
        <span class="form-input__label">
          {{ $t('reset_password.message-code') }}
        </span>
        <div
          v-if="errors.password_reset_code"
          class="form-input__error"
          v-text="errors.password_reset_code"
        />
        <div v-if="authErrors && authErrors.password_reset_code" class="form-input__error">
          <p v-for="(err, index) in authErrors.password_reset_code" :key="index">
            {{ err }}
          </p>
        </div>
      </div>
      <div
        v-if="!isStepOne"
        class="form-input"
        :class="[
          { 'form-input--full': form.password },
          { 'form-input--error': errors.password || (authErrors && authErrors.password) },
        ]"
      >
        <input v-model="form.password" :type="isHidePassword ? 'password' : 'text'" />
        <span class="form-input__label">
          {{ $t('reset_password.new_password') }}
        </span>
        <div class="form-input__icon" @click="isHidePassword = !isHidePassword">
          <img v-if="isHidePassword" src="@/assets/icons/icon-eye-closed.svg" />
          <img v-else src="@/assets/icons/icon-eye.svg" />
        </div>
        <div v-if="errors.password" class="form-input__error" v-text="errors.password" />
        <div v-if="authErrors && authErrors.password" class="form-input__error">
          <p v-for="(err, index) in authErrors.password" :key="index">
            {{ err }}
          </p>
        </div>
      </div>
      <div
        v-if="!isStepOne"
        class="form-input"
        :class="[
          { 'form-input--full': form.password_confirmation },
          {
            'form-input--error':
              errors.password_confirmation || (authErrors && authErrors.password_confirmation),
          },
        ]"
      >
        <input v-model="form.password_confirmation" :type="isHidePassword ? 'password' : 'text'" />
        <span class="form-input__label">
          {{ $t('reset_password.repeat_password') }}
        </span>
        <div
          v-if="errors.password_confirmation"
          class="form-input__error"
          v-text="errors.password_confirmation"
        />
        <div v-if="authErrors && authErrors.password_confirmation" class="form-input__error">
          <p v-for="(err, index) in authErrors.password_confirmation" :key="index">
            {{ err }}
          </p>
        </div>
      </div>
      <ButtonElement
        v-if="isStepOne"
        :text="$t('button_props.send')"
        @click-button="checkStepOne"
      />
      <ButtonElement v-else :text="$t('button_props.continue')" @click-button="resetPassword" />
    </FormElement>
  </div>
</template>

<script>
import FormElement from '../components/elements/FormElement';
import ButtonElement from '../components/elements/ButtonElement';
import formValidate from '../mixin/formValidate';

export default {
  name: 'ResetPassword',
  components: {
    FormElement,
    ButtonElement,
  },
  mixins: [formValidate],
  data() {
    return {
      isStepOne: true,
      isHidePassword: true,
      form: {
        password: null,
        password_confirmation: null,
        password_reset_code: null,
      },
      errors: {
        password: null,
        password_confirmation: null,
        password_reset_code: null,
      },
    };
  },
  computed: {
    email() {
      // Используем email из query, если он прошел проверку безопасности
      return this.$route.query.email || this.$store.state.Auth.resetPasswordEmail || null;
    },
  },
  mounted() {
    const emailFromQuery = this.$route.query.email;
    const emailFromSessionStorage = sessionStorage.getItem('resetPasswordEmail');

    // GUARD: Проверяем, что email из query совпадает с email в sessionStorage
    // Это защита от манипуляций с query параметром
    if (emailFromQuery) {
      // Если есть email в query, но нет в sessionStorage - это попытка манипуляции или переход с другого устройства
      if (!emailFromSessionStorage) {
        this.$notify({
          message: 'Ссылка недействительна. Пожалуйста, запросите код восстановления заново.',
          type: 'error',
        });
        this.$router.replace({ name: 'ForgetPassword' });
        return;
      }

      // Если email из query не совпадает с email в sessionStorage - это попытка манипуляции
      if (emailFromQuery !== emailFromSessionStorage) {
        this.$notify({
          message: 'Неверная ссылка. Пожалуйста, запросите код восстановления заново.',
          type: 'error',
        });
        this.$router.replace({ name: 'ForgetPassword' });
        return;
      }

      // Email прошел проверку - сохраняем в store
      if (emailFromQuery && !this.$store.state.Auth.resetPasswordEmail) {
        this.$store.commit('Auth/setData', { label: 'resetPasswordEmail', data: emailFromQuery });
      }
    } else if (emailFromSessionStorage) {
      // Если нет email в query, но есть в sessionStorage - используем его (страница была перезагружена)
      this.$store.commit('Auth/setData', {
        label: 'resetPasswordEmail',
        data: emailFromSessionStorage,
      });
    } else {
      // Нет email нигде - редирект на страницу забытого пароля
      this.$router.replace({ name: 'ForgetPassword' });
    }
  },
  methods: {
    checkStepOne() {
      if (!this.form.password_reset_code) {
        this.errors.password_reset_code = this.$t('reset_password.enter_code');
        return false;
      }

      if (!this.email) {
        this.$notify({
          message: 'Email not found. Please go to forget password page.',
          type: 'error',
        });
        this.$router.push({ name: 'ForgetPassword' });
        return false;
      }

      const data = {
        email: this.email,
        password_reset_code: this.form.password_reset_code,
      };

      this.$store
        .dispatch('Auth/checkCode', data)
        .then(() => {
          this.isStepOne = false;
        })
        .catch((err) => {
          // Ошибка обрабатывается в store
        });
    },
    resetPassword() {
      this.validateForm();
      if (this.isError) {
        return false;
      }

      if (!this.email) {
        this.$notify({
          message: 'Email not found. Please go to forget password page.',
          type: 'error',
        });
        this.$router.push({ name: 'ForgetPassword' });
        return false;
      }

      const data = {
        ...this.form,
        email: this.email,
      };

      this.$store
        .dispatch('Auth/resetPassword', data)
        .then(() => {
          // Очищаем email из sessionStorage и store после успешного сброса
          sessionStorage.removeItem('resetPasswordEmail');
          this.$store.commit('Auth/setData', { label: 'resetPasswordEmail', data: null });
          // Очищаем query параметры из URL
          this.$router.replace({ name: 'ResetPassword' });
          this.$router.push('/');
        })
        .catch((err) => {
          if (
            err.response &&
            err.response.data &&
            err.response.data.errors &&
            err.response.data.errors.password_reset_code
          ) {
            this.isStepOne = true;
          }
        });
    },
    validateForm() {
      if (!this.form.password) {
        this.errors.password = this.$t('reset_password.enter_password');
      } else {
        this.validatePassword();
      }

      if (!this.form.password_confirmation) {
        this.errors.password_confirmation = this.$t('reset_password.enter_password');
      } else if (this.form.password_confirmation !== this.form.password) {
        this.errors.password_confirmation = this.$t('reset_password.mismatch');
      }
    },
  },
};
</script>
